import React, { useEffect, useMemo, useState } from 'react';
import {
  DataParamFilter,
  ObjectParamsItem,
  ProcessingAction,
  StateChange,
  StateChangeType,
  stateChangeTypes,
  STORE_OBJECT,
} from 'types/rest/assetCatalogTypes';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import _ from 'lodash';
import i18n from 'i18n';
import BzFormControlWithHook from 'components/forms/BzFormControlWithHook';
import { CallbackFn, useFormInput, Validator, ValidatorType } from 'hooks/useFormInput';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Autofocus from 'components/forms/Autofocus/Autofocus';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { useDispatch } from 'react-redux';
import BlazeInputDropdown, { BlazeDropdownItem } from 'components/ui/dropdown/BlazeDropdown/BlazeInputDropdown';
import EntityModal from 'views/AssetCatalog/Transactions/TransactionEditor/ProcessingActions/stateChange/EntityModal';
import ObjectParamsList from 'views/AssetCatalog/Transactions/TransactionEditor/ProcessingActions/stateChange/ObjectParamsList';
import { callUpdateDataModel, loadDataModelForService } from 'store/dataModel/dataModelActions';
import { MeModel } from '@blazemeter/blaze-data-model';
import SimpleLoader from 'components/loaders/SimpleLoader';
import { ProcessingActionContext } from '../../../../../../contexts/ProcessingActionContext';
import { useIsMount } from '../../../../../../hooks/useIsMountHook';
import FilterByDataParameters from './FilterByDataParameters';
import store from 'store/configureStore';
import { ApplicationState } from 'store/rootReducer';
import { saveProcessingAction } from '../CommonActions';
import FullPageLoader from '../../../../../../components/loaders/FullPageLoader';

type StateChangeProps = {
  processingAction: ProcessingAction;
  onClose: () => void;
  onCreateFinished: () => void;
};

const StateChangeModal = (props: StateChangeProps) => {
  const dispatch = useDispatch();
  const [processingAction, setProcessingAction] = useState({
    ...props.processingAction,
    definition: props.processingAction.definition as StateChange,
  });
  const [showCreateEntity, setShowCreateEntity] = useState(false);
  const [entityValue, setEntityValue] = useState('');
  const [isValidParamList, setIsValidParamList] = useState(true);
  const [isValidDataEntity, setIsValidDataEntity] = useState(true);
  const [isValidFilters, setIsValidFilters] = useState(true);
  const processingActionsContext: any = React.useContext(ProcessingActionContext);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  /** Local copy of the global Scope from the context */
  const [globalScope, setGlobalScope] = useState<any>({});
  const isMount = useIsMount();

  useEffect(() => {
    if (isMount) {
      // Get models only if they are not already in the context, otherwise we can reuse them
      const isModelEmpty = _.isNil(processingActionsContext.dataModel);
      if (isModelEmpty) {
        getDataModels();
      } else {
        // Copy the original model to the "current" model
        const originalModel = _.cloneDeep(processingActionsContext.dataModel);
        processingActionsContext.setOriginalDm(originalModel);
        setIsLoading(false);

        //Copy Global Scope
        if (!_.isNil(processingActionsContext.globalScope)) {
          setGlobalScope(processingActionsContext.globalScope);
        }
      }
    }
  }, []);

  // Triggered when Global Scope is loaded by context
  useEffect(() => {
    if (!isMount && !_.isNil(processingActionsContext.globalScope)) {
      setGlobalScope(processingActionsContext.globalScope);
    }
  }, [processingActionsContext.globalScope]);

  useEffect(() => {
    //Do validation if anything changes
    doValidation();
  }, [processingAction, isLoading]);

  /**
   * Checks if provided list of parameters contains param with empty value.
   * @param parameters
   * @returns boolean indicating if there is an empty value present in the list.
   */
  const containsEmptyValues = (parameters: ObjectParamsItem[]) => {
    return (
      parameters.filter(dep => dep.value?.trim() === '').length > 0 ||
      parameters.filter(dep => dep.key === null).length > 0
    );
  };

  /**
   * Validation of update of objects from Blaze Data.
   */
  const validateObjectStateUpdate = () => {
    //Blaze Data Entity validation
    const selectedDataEntity = getSelectedDataEntity();
    if (selectedDataEntity === null && isValidDataEntity === true) {
      setIsValidDataEntity(false);
    }
    if (selectedDataEntity !== null && isValidDataEntity === false) {
      setIsValidDataEntity(true);
    }

    //Parameter validation
    const { parameters } = processingAction.definition;
    const isListWithEmptyValues = containsEmptyValues(parameters);
    if (isValidParamList === true && isListWithEmptyValues === true) {
      setIsValidParamList(false);
    }
    if (isValidParamList === false && isListWithEmptyValues === false) {
      setIsValidParamList(true);
    }
  };

  /**
   * Validation of global state update.
   */
  const validateGlobalScopeStateUpdate = () => {
    const { parameters } = processingAction.definition;
    const paramsCount = _.size(parameters);
    //Check if each parameter present in DSL exists in current global scope
    let isDslParamListValid = true;
    _.forEach(parameters, param => {
      if (isDslParamListValid === true) {
        isDslParamListValid = !_.isNil(globalScope[param.key]);
      }
    });

    //In case of increment, make sure there is a value set in the input
    const { objectAction } = processingAction.definition;
    let isValidIncrement = true;
    if (objectAction === 'INCREMENT_VALUE') {
      isValidIncrement = !containsEmptyValues(parameters);
    }

    if (paramsCount > 0 && isDslParamListValid && isValidIncrement) {
      setIsValidParamList(true);
    } else {
      setIsValidParamList(false);
    }
  };

  const doValidation = () => {
    const { objectAction } = processingAction.definition;
    const isGlobalScopeAction = isGlobalAction(objectAction);
    //Entity validation
    if (!isGlobalScopeAction) {
      validateObjectStateUpdate();
    } else {
      validateGlobalScopeStateUpdate();
    }
  };

  /**
   * Loads data model and global scope from asset repository.
   */
  const getDataModels = async () => {
    try {
      await processingActionsContext.fetchDataModels();
    } catch (e) {
      console.log('Problem in fetching data model:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const isGlobalAction = (actionType: StateChangeType) => {
    return actionType === 'UPDATE_VALUE' || actionType === 'INCREMENT_VALUE';
  };

  const nameValidators: Validator[] = [
    {
      type: ValidatorType.REQUIRED,
      invalidMessage: i18n.t('transactions:processingActions.modal.stateChange.nameRequired'),
    },
    {
      type: ValidatorType.MAX_LENGTH,
      value: 250,
      invalidMessage: i18n.t('transactions:processingActions.modal.validation.nameMaxLength'),
    },
    {
      type: ValidatorType.INVALID_LEADING_SPACES,
      invalidMessage: i18n.t('transactions:processingActions.modal.validation.nameIsInvalid'),
    },
    {
      type: ValidatorType.INVALID_CHARACTERS,
      invalidMessage: i18n.t('transactions:processingActions.modal.validation.invalidChars'),
      value: /^(?![0-9])[a-zA-Z0-9$_]+$/,
    },
  ];

  const setModalValue = (showCreate: boolean, entityValue: string) => {
    setEntityValue(entityValue);
    setShowCreateEntity(showCreate);
  };

  /**
   * Adds new value to the DSL definition.
   * @param field
   * @param value
   */
  const updateStateChange = (field: string, value: any) => {
    const stateChange: StateChange = _.cloneDeep(processingAction.definition);
    if (stateChange !== undefined) {
      _.set(stateChange, field, value);
      setProcessingAction({ ...processingAction, definition: stateChange });
    }
  };

  const onChangeName = (value: string) => {
    setProcessingAction({ ...processingAction, name: value });
  };

  const onSelectStateChangeType = (type: BlazeDropdownItem) => {
    const stateChange: StateChange = _.cloneDeep(processingAction.definition);
    if (stateChange !== undefined) {
      stateChange.objectAction = type.id as StateChangeType;
      stateChange.parameters = [];
      buildDataEntityRows(stateChange);
      if (type.id !== 'UPDATE_OBJECT' && isValidFilters === false) {
        setIsValidFilters(true);
      }
      if (type.id === 'DELETE_OBJECT' && isValidParamList === false) {
        setIsValidParamList(true);
      }
      if (type.id === 'UPDATE_VALUE' || type.id === 'INCREMENT_VALUE') {
        processingActionsContext.setCurrentEntity(null); //they use Global Scope - Data Parameters
        stateChange.model = '';
        if (isValidDataEntity === false) {
          setIsValidDataEntity(true);
        }
      } else {
        processingActionsContext.setCurrentEntity(stateChange.model);
      }
      setProcessingAction({ ...processingAction, definition: stateChange });
    }
  };

  /**
   * Gets list of parameters and their value from DSL model.
   * @param modelName
   */
  const getObjectDataParamsList = (modelName: string): BlazeDropdownItem[] => {
    const entities = processingActionsContext.getEntities();
    if (_.isNil(modelName) || _.isNil(entities)) {
      return [];
    }

    //Map entity properties, which represent our parameters
    const selectedEntity = entities[modelName];
    if (_.isNil(selectedEntity) || _.isNil(selectedEntity.properties)) {
      return [];
    }

    return _.map(entities[modelName].properties, (value, key) => ({ id: key, name: key }));
  };

  function buildDataEntityRows(stateChange: StateChange) {
    const definition = props.processingAction.definition as StateChange;
    stateChange.filters = [];
    if (definition.objectAction === stateChange.objectAction && definition.model === stateChange.model) {
      stateChange.parameters = definition.parameters ? definition.parameters : [];
      stateChange.filters = definition.filters ? definition.filters : [];
    }
    if (stateChange.parameters.length === 0 && stateChange.objectAction === 'STORE_OBJECT') {
      stateChange.parameters = getObjectDataParamsList(stateChange.model).map((dpl, index) => {
        return { key: dpl.id, value: '', id: (index as unknown) as string } as ObjectParamsItem;
      });
    }
    return stateChange;
  }

  const onChangeDataEntity = (dataEntityItem: string) => {
    processingActionsContext.setCurrentEntity(dataEntityItem);
    const stateChange: StateChange = _.cloneDeep(processingAction.definition);
    stateChange.model = dataEntityItem as string;
    stateChange.parameters = [];
    buildDataEntityRows(stateChange);
    setProcessingAction({ ...processingAction, definition: stateChange });
  };

  const getSelectedType = (): BlazeDropdownItem => {
    const type = stateChangeTypes.find(item => item.id === processingAction.definition.objectAction);
    return type ? type : STORE_OBJECT;
  };

  /**
   * Gets list of global scope params and maps it to the blaze input dropdown item.
   */
  const getListOfGlobalScopeParams = (): BlazeDropdownItem[] => {
    if (_.isNil(globalScope)) {
      return [];
    }
    return _.map(globalScope, (value, key) => {
      return {
        id: key,
        name: key,
      };
    });
  };

  /**
   * Maps list of entities from the data model to the blaze input dropdown.
   * */
  const renderListOfEntities = (): BlazeDropdownItem[] => {
    const entities = processingActionsContext.getEntities();
    if (!entities) {
      return [];
    }

    const list = _.map(entities, (entity, key) => ({
      id: key,
      name: entity.title,
    }));

    return list;
  };

  /**
   * Get selected entity in the definition and compares it with available entities in TDM.
   * If entity from definition does not exist it returns null.
   */
  const getSelectedDataEntity = (): BlazeDropdownItem | null => {
    const dataEntity = _.find(renderListOfEntities(), item => item.id === processingAction.definition.model);
    processingActionsContext.setCurrentEntity(dataEntity ? processingAction.definition.model : null);
    return dataEntity ? dataEntity : null;
  };

  // Use memoized value
  const selectedDataEntity = useMemo(() => getSelectedDataEntity(), [processingAction, processingActionsContext]);

  const nameInput = useFormInput(
    processingAction.name ? processingAction.name : '',
    (fn: CallbackFn) => {
      onChangeName(fn.value as string);
    },
    ...nameValidators,
  );

  const saveDataModel = async () => {
    const response = await callUpdateDataModel(_.cloneDeep(processingActionsContext.dataModel));
    if (response.status !== 200) {
      dispatch(showToastNotification(ToastType.Danger, i18n.t('common:tdm.dmUpdateFailed'), ''));
      return;
    } else {
      const selectedService = (store.getState() as ApplicationState).ui.pageFilter.serviceId;
      if (processingActionsContext.transactionServiceId === selectedService) {
        dispatch(loadDataModelForService(selectedService, true));
      }
    }
    processingActionsContext.setOriginalDm(response.body.result as MeModel);
  };

  const onSave = async () => {
    const keyList = (processingAction.definition as StateChange).parameters.map(p => p.key);
    const keySet = new Set(keyList);
    if (keyList.length !== keySet.size) {
      setIsSaving(false);
      const duplicated = _.values(_.groupBy(keyList))
        .map(d => ({ name: d[0], count: d.length }))
        .filter(m => m.count > 1)
        .map(f => ' ' + f.name)
        .toString();
      dispatch(
        showToastNotification(
          ToastType.Danger,
          'Please assign only one Value to a Data Parameter',
          'The following Data Parameter(s) have multiple values assigned:' + duplicated,
        ),
      );
      return;
    }

    const { objectAction } = processingAction.definition;

    // Save models based on action type
    if (isGlobalAction(objectAction)) {
      await processingActionsContext.saveGlobalScope(globalScope);
    } else {
      await saveDataModel();
    }

    try {
      await saveProcessingAction(processingAction, 'STATE_UPDATE');
    } catch (e) {
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('errorMsgs.unexpectedError'),
          i18n.t('transactions:processingActions.modal.error.saveEdit', {
            reason: e.response ? e.response.data.error : 'Something went wrong.',
          }),
        ),
      );
    }
    props.onClose();
    props.onCreateFinished();
  };

  const addNewItemInParamRow = (key: string, index: number) => {
    let list = [{ key: key, value: '' }] as ObjectParamsItem[];
    if (processingAction.definition.parameters.length > 0) {
      list = _.cloneDeep(processingAction.definition.parameters as ObjectParamsItem[]);
      list[index].key = key;
    }
    updateStateChange('parameters', list);
  };

  const selectNewItemInFilterRow = (key: string, index: number) => {
    if (processingAction.definition.filters) {
      const list = _.cloneDeep(processingAction.definition.filters as DataParamFilter[]);
      list[index].key = key;
      updateStateChange('filters', list);
    }
  };

  /** Extracts list of params from state update definition. If there is none, return blank one. */
  const getDataParametersFromDefinition = () => {
    const { parameters } = processingAction.definition;
    const isParamsEmpty = parameters.length === 0;
    return isParamsEmpty ? ([{ key: null, value: '' }] as ObjectParamsItem[]) : parameters;
  };

  /**
   * Adds new item in to the global scope map.
   * @param key
   */
  const addNewGlobalEntityItem = (key: string) => {
    const globalScopeUpdate = _.set(globalScope, key, '');
    setGlobalScope(globalScopeUpdate);
  };

  const renderStateChangeTypePanel = () => {
    const action = processingAction.definition.objectAction;

    //We do not need panel for delete object action
    if (processingAction.definition.objectAction === 'DELETE_OBJECT') {
      return;
    }

    const isGlobalScopeUpdate = isGlobalAction(action);

    const panelContent = (
      <ObjectParamsList
        key={'update_object'}
        stateChangeType={action}
        dataParams={getDataParametersFromDefinition()}
        onListChange={list => updateStateChange('parameters', list)}
        availableDataParams={
          isGlobalScopeUpdate
            ? getListOfGlobalScopeParams()
            : getObjectDataParamsList(processingAction.definition.model)
        }
        createEntityCallBack={(result: any) => {
          //Regular update of current DSL parameters
          addNewItemInParamRow(result.key, result.index);

          //Update of global entity object
          if (isGlobalScopeUpdate) {
            addNewGlobalEntityItem(result.key);
          }
        }}
        showPlaceholder={isGlobalScopeUpdate ? false : selectedDataEntity === null}
        entityType={isGlobalScopeUpdate ? 'GLOBAL_SCOPE_PARAMETER' : 'DATA_PARAMETER'}
      />
    );

    //Wrap panel content with loader
    if (isLoading) {
      return (
        <div className={'position-relative'}>
          <div className="blur-background">
            <FullPageLoader loadingText={'Loading data...'} />
          </div>
          <div className={isLoading ? 'blur' : undefined}>{panelContent}</div>
        </div>
      );
    }

    return panelContent;
  };

  const renderStateChangeFilter = () => {
    if (selectedDataEntity === null) {
      return;
    }
    switch (processingAction.definition.objectAction) {
      case 'UPDATE_OBJECT':
      case 'DELETE_OBJECT':
        const dpfilters =
          processingAction.definition.filters && processingAction.definition.filters.length > 0
            ? processingAction.definition.filters
            : ([{ key: null, operation: null, values: [''] }] as DataParamFilter[]);
        return (
          <FilterByDataParameters
            updateList={(list: DataParamFilter[]) => updateStateChange('filters', list)}
            dataParamsList={getObjectDataParamsList(processingAction.definition.model)}
            dataParamFilters={dpfilters}
            isValid={isValidFilters}
            setIsValid={setIsValidFilters}
            createEntityCallBack={(result: any) => {
              selectNewItemInFilterRow(result.key, result.id);
            }}
          />
        );
    }
  };

  const getDataEntityDropDown = () => {
    if (isLoading === true) {
      return (
        <div style={{ marginTop: '3px', marginBottom: '0px' }}>
          <SimpleLoader style={{ display: 'inline' }} showLoadingText={true} loaderSize={15} />
        </div>
      );
    }
    switch (processingAction.definition.objectAction) {
      case 'STORE_OBJECT':
      case 'UPDATE_OBJECT':
      case 'DELETE_OBJECT':
        return (
          <>
            <BlazeInputDropdown
              id={'stateUpdateDataEntity'}
              minDropdownMenuWidth={290}
              maxDropdownHeight={250}
              items={renderListOfEntities()}
              isDefaultOptionSelectable={false}
              onClick={(item: BlazeDropdownItem) => onChangeDataEntity(item.id as string)}
              selected={selectedDataEntity}
              isSearchable={true}
              dropDownFooter={{
                entity: 'Data Entity',
                onClick: (entityValue: string) => setModalValue(true, entityValue),
                style: 'Text',
              }}
            />
            <p className="text-muted small" style={{ marginBottom: '-8px' }}>
              {i18n.t('transactions:processingActions.modal.stateChange.selectDataEntityDesc')}
            </p>
          </>
        );
      case 'UPDATE_VALUE':
      case 'INCREMENT_VALUE':
        return (
          <p style={{ marginTop: '3px', marginBottom: '0px' }}>
            {i18n.t('transactions:processingActions.modal.stateChange.globalScope')}
          </p>
        );
    }
  };

  const getModalBody = () => (
    <>
      <div className={'text-right mt-1'}>
        {showCreateEntity && (
          <EntityModal
            availableItems={renderListOfEntities().map(entity => entity.name)}
            itemName={entityValue}
            closeModalAction={() => setShowCreateEntity(false)}
            createEntityCallBack={onChangeDataEntity}
            entityType={'DATA_ENTITY'}
          />
        )}
      </div>
      <div>
        <p>{i18n.t('transactions:processingActions.modal.stateChange.description')}</p>
        <label htmlFor="stateChangeName">{i18n.t('transactions:processingActions.modal.name')}</label>
        <Autofocus>
          <BzFormControlWithHook
            id={'stateChangeName'}
            useFormInputHook={nameInput}
            placeholder={i18n.t('transactions:processingActions.modal.stateChange.enterName')}
            required={true}
            hideErrors={false}
            autoComplete={'off'}
          />
        </Autofocus>
        <hr />
        <Row>
          <Col md={4}>
            <label htmlFor="stateChangeType">{i18n.t('transactions:processingActions.modal.stateChange.type')}</label>
            <BlazeInputDropdown
              id={'stateChangeType'}
              minDropdownMenuWidth={290}
              maxDropdownHeight={300}
              items={stateChangeTypes}
              onClick={onSelectStateChangeType}
              selected={getSelectedType()}
              showGroups={true}
            ></BlazeInputDropdown>
          </Col>
          <Col md={4}>
            <label htmlFor="dataEntity">{i18n.t('transactions:processingActions.modal.stateChange.dataEntity')}</label>
            {getDataEntityDropDown()}
          </Col>
          <Col md={4}></Col>
        </Row>
        {renderStateChangeFilter()}
        {renderStateChangeTypePanel()}
      </div>
    </>
  );

  const validModel: boolean = nameInput.valid && isValidDataEntity && isValidParamList && isValidFilters;
  return (
    <Modal
      show={true}
      onHide={props.onClose}
      enforceFocus={false}
      animation={false}
      backdropClassName={'backdrop-faded'}
      size={'lg'}
    >
      <Modal.Header closeButton id="qa-modal-assign-template-btn-close-icon" style={{ marginBottom: '-15px' }}>
        <h5 className="modal-title">{i18n.t('transactions:processingActions.modal.stateChange.title')}</h5>
      </Modal.Header>

      <Modal.Body>{getModalBody()}</Modal.Body>

      <Modal.Footer>
        <Button variant={'link'} onClick={props.onClose}>
          {i18n.t('transactions:processingActions.modal.cancel')}
        </Button>
        {isSaving === true ? (
          <Button disabled={true}>
            <>
              <SimpleLoader className={'inline-span-style'} loadingText={''} />
              <span className={'ml-2 inline-span-style'}>Saving...</span>
            </>
          </Button>
        ) : (
          <Button
            disabled={!validModel}
            onClick={() => {
              setIsSaving(true);
              onSave();
            }}
          >
            {i18n.t('transactions:processingActions.modal.save')}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default StateChangeModal;
